export type LanguageStatus =
  | "enabled"
  | "draft"
  | "planned"
  | "next"
  | "disabled";

export type TranslationProvenance =
  | "source_english"
  | "machine_assisted_draft"
  | "human_draft"
  | "professionally_reviewed"
  | null;

export type ProfessionalReviewStatus =
  | "source_baseline"
  | "not_started"
  | "not_reviewed"
  | "in_review"
  | "reviewed"
  | "rejected";

export type ProfessionalReview = {
  status: ProfessionalReviewStatus;
  reviewedOn: string | null;
  reviewerReference: string | null;
  notes?: string;
};

export type LanguageAvailability = {
  /** Owner-approved exposure in controlled soft-launch UI (≠ professionally reviewed). */
  softLaunch: boolean;
};

export type LanguageDefinition = {
  code: string;
  englishName: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  script: string;
  status: LanguageStatus;
  localeFile: string | null;
  availability?: LanguageAvailability;
  translationProvenance?: TranslationProvenance;
  professionalReview?: ProfessionalReview;
  notes?: string;
};

export type LanguagesCatalog = {
  version: number;
  schema?: string;
  defaultLanguage: string;
  softLaunchException?: { active: boolean; notes: string };
  notes?: string[];
  languages: LanguageDefinition[];
};

import languagesCatalog from "@content/locales/languages.json";

export const languagesConfig = languagesCatalog as LanguagesCatalog;

const SOFT_LAUNCH_ORDER = ["en", "es", "zh-Hans", "yue", "ko", "vi"];

/** Languages selectable in the UI (soft-launch availability). */
export function getSelectableLanguages(): LanguageDefinition[] {
  const selectable = languagesConfig.languages.filter(
    (l) => l.availability?.softLaunch === true && typeof l.localeFile === "string",
  );
  return selectable.sort(
    (a, b) =>
      SOFT_LAUNCH_ORDER.indexOf(a.code) - SOFT_LAUNCH_ORDER.indexOf(b.code),
  );
}

/** @deprecated Alias — use getSelectableLanguages */
export function getEnabledLanguages(): LanguageDefinition[] {
  return getSelectableLanguages();
}

export function getLanguage(code: string): LanguageDefinition | undefined {
  return languagesConfig.languages.find((l) => l.code === code);
}

export function isLanguageSelectable(code: string): boolean {
  const lang = getLanguage(code);
  return Boolean(
    lang && lang.availability?.softLaunch === true && lang.localeFile,
  );
}

/** @deprecated Alias — use isLanguageSelectable */
export function isLanguageEnabled(code: string): boolean {
  return isLanguageSelectable(code);
}

export function isProfessionallyReviewed(code: string): boolean {
  const lang = getLanguage(code);
  if (!lang) return false;
  if (lang.code === "en") return false;
  return (
    lang.translationProvenance === "professionally_reviewed" ||
    lang.professionalReview?.status === "reviewed"
  );
}

/** Non-English soft-launch languages still awaiting professional review. */
export function showsTranslationPreview(code: string): boolean {
  return (
    isLanguageSelectable(code) &&
    code !== languagesConfig.defaultLanguage &&
    !isProfessionallyReviewed(code)
  );
}
